import { SlidersHorizontal } from 'lucide-react'
import { Popover as PopoverPrimitive } from 'radix-ui'
import { useState, type ComponentType, type ReactNode } from 'react'
import { Pills } from '@/components/Pills'
import { Dialog, DialogClose, DialogContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { useIsPhone } from '@/lib/platform'
import { cn } from '@/lib/utils'

// One way of narrowing or laying out a page, as a row of pills: a heading over them, and the group's name for screen
// readers. `usual` is the setting most people leave it on; any other turns the button's dot on.
export type Filter = {
  heading: string
  label: string
  value: string
  options: [string, string][]
  onChange: (value: string) => void
  usual?: string
  icons?: Record<string, ComponentType<{ className?: string }>>
}

// How a page is shown and whose things it shows, kept behind one round button so the page starts with its content:
// a sheet from the bottom edge on a phone, a small panel under the button on a computer. A dot on the button says
// something other than the usual is picked, so a narrowed page never looks like everything.
// `footer` follows the filters (a link to the page's settings).
export function FilterButton({
  title,
  filters,
  className,
  footer,
  iconOnly,
}: {
  title: string
  filters: Filter[]
  className?: string
  footer?: ReactNode
  // Just the icon at every size, for a group of round buttons (Home's "+", view and Filter).
  iconOnly?: boolean
}) {
  const phone = useIsPhone()
  const [open, setOpen] = useState(false)
  const changed = filters.some((x) => x.usual !== undefined && x.value !== x.usual)
  const button = (
    <button
      type="button"
      className={cn(
        'relative inline-flex size-11 shrink-0 items-center justify-center gap-2 rounded-full bg-secondary text-sm font-medium outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 nav:h-10',
        !iconOnly && 'nav:w-auto nav:px-4',
        className,
      )}
    >
      <SlidersHorizontal className="size-4.5" aria-hidden />
      <span className={iconOnly ? 'sr-only' : 'sr-only nav:not-sr-only'}>{title}</span>
      {changed && (
        <>
          <span
            aria-hidden
            className={cn(
              'absolute top-2 right-2 size-2 rounded-full bg-foreground ring-2 ring-secondary',
              !iconOnly && 'nav:static nav:ring-0',
            )}
          />
          <span className="sr-only">(changed)</span>
        </>
      )}
    </button>
  )
  const body = (
    <div className="flex flex-col gap-5">
      {filters.map((filter) => (
        <div key={filter.label} className="flex flex-col gap-2">
          <p className="eyebrow" aria-hidden>
            {filter.heading}
          </p>
          {filter.options.length > MANY ? (
            <Chips filter={filter} />
          ) : (
            <div className="flex">
              <Pills
                label={filter.label}
                value={filter.value}
                options={filter.options}
                icons={filter.icons}
                onChange={filter.onChange}
              />
            </div>
          )}
        </div>
      ))}
      {footer}
    </div>
  )

  if (!phone)
    return (
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Trigger asChild>{button}</PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="end"
            sideOffset={8}
            aria-label={title}
            className="z-50 rounded-3xl bg-popover p-5 text-popover-foreground shadow-lift outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0"
          >
            {body}
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    )

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{button}</DialogTrigger>
      <DialogContent showCloseButton={false} aria-describedby={undefined} className="gap-6 pt-9">
        <div className="flex items-center justify-between gap-4">
          <DialogTitle className="text-title">{title}</DialogTitle>
          <DialogClose className="-mr-2 min-h-11 rounded-full px-3 font-semibold outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50">
            Done
          </DialogClose>
        </div>
        {body}
      </DialogContent>
    </Dialog>
  )
}

// Past a few options (years, people), one pill that scrolls sideways hides most of them, so they wrap onto as many
// rows as they need instead, each its own chip.
const MANY = 4

function Chips({ filter }: { filter: Filter }) {
  return (
    <div role="group" aria-label={filter.label} className="flex max-w-[22rem] flex-wrap gap-1.5">
      {filter.options.map(([option, name]) => (
        <button
          key={option}
          type="button"
          aria-pressed={filter.value === option}
          onClick={() => filter.onChange(option)}
          className="min-h-9 rounded-full bg-secondary px-3.5 text-sm font-medium text-muted-foreground tabular-nums transition-colors outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-pressed:bg-brand aria-pressed:text-brand-foreground pointer-coarse:min-h-11"
        >
          {name}
        </button>
      ))}
    </div>
  )
}
